import React, { useState, useEffect } from 'react';
import {
    BarChart3, Users, ShoppingCart, DollarSign, Package, TrendingUp,
    Clock, CheckCircle, Truck, Eye, ArrowRight, RefreshCw,
    Loader2, UserPlus, Store, CreditCard, AlertCircle, Calendar
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { adminApi } from '@/services/api/admin';
import { formatPrice, formatDate } from '@/utils/formatters';
import { useNavigate } from 'react-router-dom';


interface AdminDashboardProps {
    onNavigateTo?: (path: string) => void;
}

const AdminDashboardComponent: React.FC<AdminDashboardProps> = ({
    onNavigateTo
}) => {
    const [dashboardData, setDashboardData] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const navigate = useNavigate();

    const fetchDashboardData = async () => {
        setIsLoading(true);
        try {
            const response = await adminApi.getDashboardStats();
            setDashboardData(response);
        } catch (error: any) {
            console.error('Error loading dashboard:', error);
            toast.error('Error al cargar el dashboard');
        } finally {
            setIsLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchDashboardData();
    };

    const handleQuickAction = (path: string) => {
        if (onNavigateTo) {
            onNavigateTo(path);
        } else {
            navigate(path);
        }
    };

    const getStatusColor = (status: string) => {
        const colors = {
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
            processing: 'bg-blue-100 text-blue-800 border-blue-200',
            shipped: 'bg-purple-100 text-purple-800 border-purple-200',
            delivered: 'bg-green-100 text-green-800 border-green-200',
            cancelled: 'bg-red-100 text-red-800 border-red-200'
        };
        return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    const getStatusIcon = (status: string) => {
        const icons = {
            pending: <Clock className="w-4 h-4" />,
            processing: <RefreshCw className="w-4 h-4" />,
            shipped: <Truck className="w-4 h-4" />,
            delivered: <CheckCircle className="w-4 h-4" />,
            cancelled: <AlertCircle className="w-4 h-4" />
        };
        return icons[status as keyof typeof icons] || <Clock className="w-4 h-4" />;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando dashboard...</p>
                </div>
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No se pudieron cargar los datos del dashboard</p>
                    <button
                        onClick={handleRefresh}
                        className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                                <BarChart3 className="w-6 h-6 mr-2" />
                                Dashboard Principal
                            </h1>
                            <p className="mt-1 text-gray-600">
                                Visión general del performance del marketplace
                            </p>
                        </div>
                        <div className="mt-4 sm:mt-0 flex space-x-3">
                            <button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                {refreshing ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                )}
                                Actualizar
                            </button>
                            <button className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors">
                                <Calendar className="w-4 h-4 mr-2" />
                                Ver Reporte Completo
                            </button>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <button
                        onClick={() => handleQuickAction('/admin/orders')}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-left hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <ShoppingCart className="w-8 h-8 text-blue-600" />
                            <ArrowRight className="w-5 h-5 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Ver Órdenes</h3>
                        <p className="text-sm text-gray-600">Gestionar pedidos y envíos</p>
                    </button>

                    <button
                        onClick={() => handleQuickAction('/admin/users')}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-left hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <Users className="w-8 h-8 text-green-600" />
                            <ArrowRight className="w-5 h-5 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Gestión de Usuarios</h3>
                        <p className="text-sm text-gray-600">Administrar clientes y vendedores</p>
                    </button>

                    <button
                        onClick={() => handleQuickAction('/admin/products')}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-left hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <Package className="w-8 h-8 text-purple-600" />
                            <ArrowRight className="w-5 h-5 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Productos</h3>
                        <p className="text-sm text-gray-600">Gestionar inventario y listados</p>
                    </button>

                    <button
                        onClick={() => handleQuickAction('/admin/analytics')}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-left hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <TrendingUp className="w-8 h-8 text-orange-600" />
                            <ArrowRight className="w-5 h-5 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Analytics</h3>
                        <p className="text-sm text-gray-600">Métricas y reportes detallados</p>
                    </button>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <DollarSign className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Ingresos Hoy</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {formatPrice(dashboardData.revenue_today)}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">Ventas del día</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <UserPlus className="w-6 h-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Nuevos Usuarios</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {dashboardData.new_users_today}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">Registrados hoy</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Store className="w-6 h-6 text-purple-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Nuevos Vendedores</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {dashboardData.new_sellers_today}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">Solicitudes hoy</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <ShoppingCart className="w-6 h-6 text-orange-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Órdenes Pendientes</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {dashboardData.orders_by_status.pending || 0}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">Por procesar</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Órdenes Recientes */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <ShoppingCart className="w-5 h-5 mr-2" />
                                Órdenes Recientes
                            </h3>
                            <button
                                onClick={() => handleQuickAction('/admin/orders')}
                                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
                            >
                                Ver todas <ArrowRight className="w-4 h-4 ml-1" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            {dashboardData.recent_orders.slice(0, 5).map((order: any) => (
                                <div key={order.id} className="flex items-center justify-between p-3 border rounded-lg">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2">
                                            <span className="text-sm font-medium text-gray-900">
                                                {order.order_number}
                                            </span>
                                            {order.status && (
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                                                    {getStatusIcon(order.status)}
                                                    <span className="ml-1 capitalize">{order.status}</span>
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-600">
                                            {order.first_name} {order.last_name}
                                        </p>
                                        {order.created_at && (
                                            <p className="text-xs text-gray-500">
                                                {formatDate(order.created_at)}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-bold text-gray-900">
                                            {formatPrice(parseFloat(order.total_amount))}
                                        </p>
                                        <button
                                            onClick={() => handleQuickAction(`/admin/orders/${order.id}`)}
                                            className="text-primary-600 hover:text-primary-700 text-xs"
                                        >
                                            <Eye className="w-3 h-3 inline mr-1" />
                                            Ver
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {dashboardData.recent_orders.length === 0 && (
                                <p className="text-gray-500 text-sm text-center py-8">
                                    No hay órdenes recientes
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Usuarios Recientes */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Users className="w-5 h-5 mr-2" />
                                Usuarios Recientes
                            </h3>
                            <button
                                onClick={() => handleQuickAction('/admin/users')}
                                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
                            >
                                Ver todos <ArrowRight className="w-4 h-4 ml-1" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            {dashboardData.recent_users.slice(0, 5).map((user: any) => (
                                <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                                            <span className="text-primary-600 font-medium text-sm">
                                                {user.first_name[0]}{user.last_name[0]}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {user.first_name} {user.last_name}
                                            </p>
                                            <p className="text-sm text-gray-600">{user.email}</p>
                                            <p className="text-xs text-gray-500">
                                                {formatDate(user.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                    {user.user_type && (
                                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full capitalize">
                                            {user.user_type}
                                        </span>
                                    )}
                                </div>
                            ))}
                            {dashboardData.recent_users.length === 0 && (
                                <p className="text-gray-500 text-sm text-center py-8">
                                    No hay usuarios recientes
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Estadísticas de Órdenes */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                        <BarChart3 className="w-5 h-5 mr-2" />
                        Distribución de Órdenes
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {Object.entries(dashboardData.orders_by_status).map(([status, count]: any) => (
                            <div key={status} className="text-center">
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="text-2xl font-bold text-gray-900 mb-1">
                                        {count}
                                    </div>
                                    <div className="text-sm font-medium text-gray-600 capitalize">
                                        {status}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Revenue Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium opacity-90">Esta Semana</p>
                                <p className="text-2xl font-bold">
                                    {formatPrice(dashboardData.revenue_this_week)}
                                </p>
                            </div>
                            <TrendingUp className="w-8 h-8 opacity-90" />
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-sm p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium opacity-90">Este Mes</p>
                                <p className="text-2xl font-bold">
                                    {formatPrice(dashboardData.revenue_this_month)}
                                </p>
                            </div>
                            <DollarSign className="w-8 h-8 opacity-90" />
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-sm p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium opacity-90">Promedio por Orden</p>
                                <p className="text-2xl font-bold">
                                    {formatPrice(dashboardData.average_order_value || 0)}
                                </p>
                            </div>
                            <CreditCard className="w-8 h-8 opacity-90" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboardComponent;